import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import { Checkbox } from "../../../ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../../ui/table";
import { epicengageSupabasev1 } from "../../../../integrations/supabase/client";
import { useMerchant } from "../../../../hooks/useMerchantEngageV1";
import { isValidEmail, STATUS_LABEL, type CustomerStatus } from "../../../../lib/customersEngageV1";

import {
  Users, Upload, Search, FileSpreadsheet, Trash2, AlertTriangle, Check,
  MapPin, ExternalLink, AlertCircle, Mail, MessageSquare, Loader2, Calendar as CalendarIcon, Clock,
} from "lucide-react";
import { Calendar } from "../../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { format } from "date-fns";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../../../ui/tooltip";
import { Switch } from "../../../ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import { Textarea } from "../../../ui/textarea";
import { Label } from "../../../ui/label";
import { Skeleton } from "../../../ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../../ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "../../../../lib/utils";
import { RichEmailEditor } from "../components/editor/RichEmailEditor";

type Outlet = {
  id: string;
  name: string;
  address: string | null;
  google_review_url: string | null;
};

type Channel = "email" | "sms" | null;

type SequenceStep = {
  label: string;
  day: number;
  subject: string;
  body: string;
  ctaLabel: string;
};

const DEFAULT_SEQUENCE_STEPS: SequenceStep[] = [
  {
    label: "Initial send",
    day: 0,
    subject: "We'd love your feedback on your visit to {{outlet_name}}",
    body: `<p>Hi {{name}},</p><p>We recently had the chance to serve you at <strong>{{outlet_name}}</strong> and we'd love to hear how it went.</p><p>Tap below to share — there might even be a little surprise waiting.</p>`,
    ctaLabel: "Share Your Experience",
  },
  {
    label: "Follow-up",
    day: 7,
    subject: "A quick nudge from {{outlet_name}}",
    body: `<p>Hi {{name}},</p><p>We know life gets busy. Just a gentle nudge — we'd love your feedback on your visit to <strong>{{outlet_name}}</strong>.</p><p>It only takes a moment.</p>`,
    ctaLabel: "Leave a Quick Review",
  },
  {
    label: "Final nudge",
    day: 14,
    subject: "One last hello from {{outlet_name}}",
    body: `<p>Hi {{name}},</p><p>This is the last time we'll reach out. If you ever feel like sharing, the link below is always open.</p><p>Thank you for visiting <strong>{{outlet_name}}</strong>.</p>`,
    ctaLabel: "Share Your Experience",
  },
];

const renderTemplate = (
  text: string,
  ctx: { name: string; outlet_name: string; first_name?: string },
) =>
  text
    .replace(/\{\{\s*first_name\s*\}\}/gi, ctx.first_name ?? ctx.name)
    .replace(/\{\{\s*name\s*\}\}/gi, ctx.name)
    .replace(/\{\{\s*outlet_name\s*\}\}/gi, ctx.outlet_name);

const shortenForSms = (
  body: string,
  ctaLabel: string,
  url: string,
  ctx: { name: string; outlet_name: string },
) => {
  const linkPart = ` ${url}`;
  const max = 160 - linkPart.length;
  const rendered = renderTemplate(body, ctx).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const trimmed =
    rendered.length > max ? rendered.slice(0, Math.max(0, max - 1)).trimEnd() + "…" : rendered;
  return `${trimmed}${linkPart}`;
};
const SummaryRow = ({
  label, value, sub,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
}) => (
  <div className="flex items-start justify-between gap-4 px-5 py-4">
    <div className="text-sm text-muted-foreground">{label}</div>
    <div className="text-sm font-medium text-right min-w-0 flex flex-col items-end gap-0.5">
      <div>{value}</div>
      {sub && <div className="text-xs text-muted-foreground font-normal">{sub}</div>}
    </div>
  </div>
);


const STEP_LABELS = [
  "Choose Audience",
  "Select Outlet",
  "Choose Channel",
  "Preview & Edit",
  "Review & Send",
];

const TOTAL_STEPS = 5;

type CustomerRow = {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  status: string | null;
};

type UploadRow = {
  rowId: string;
  name: string;
  email: string;
  phone: string;
};

type Mode = "existing" | "upload" | null;

const StepIndicator = ({ step, label }: { step: number; label: string }) => (
  <div className="flex items-center gap-3">
    <div className="flex items-center gap-2">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <div
            key={n}
            className={cn(
              "h-2 rounded-full transition-all",
              active ? "w-8 bg-primary" : done ? "w-6 bg-primary/60" : "w-6 bg-muted",
            )}
          />
        );
      })}
    </div>
    <span className="text-sm text-muted-foreground">
      Step {step} of {TOTAL_STEPS} — <span className="text-foreground font-medium">{label}</span>
    </span>
  </div>
);

const ChoiceCard = ({
  selected, onClick, icon: Icon, title, description,
}: {
  selected: boolean;
  onClick: () => void;
  icon: typeof Users;
  title: string;
  description: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "group flex-1 text-left rounded-xl border-2 p-6 transition-all relative",
      selected
        ? "border-primary bg-primary/5 shadow-sm"
        : "border-border bg-card hover:border-primary/40 hover:bg-muted/30",
    )}
  >
    {selected && (
      <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
        <Check className="h-3.5 w-3.5" />
      </div>
    )}
    <div className={cn(
      "h-11 w-11 rounded-lg flex items-center justify-center mb-4",
      selected ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
    )}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="font-semibold text-base">{title}</div>
    <div className="text-sm text-muted-foreground mt-1">{description}</div>
  </button>
);

const ChannelCard = ({
  selected, disabled, disabledReason, onClick, icon: Icon, title, description, count, countLabel,
}: {
  selected: boolean;
  disabled: boolean;
  disabledReason: string;
  onClick: () => void;
  icon: typeof Users;
  title: string;
  description: string;
  count: number;
  countLabel: string;
}) => {
  const card = (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn(
        "group flex-1 text-left rounded-xl border-2 p-6 transition-all relative w-full",
        disabled
          ? "border-border bg-muted/30 opacity-60 cursor-not-allowed"
          : selected
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-card hover:border-primary/40 hover:bg-muted/30",
      )}
    >
      {selected && !disabled && (
        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
          <Check className="h-3.5 w-3.5" />
        </div>
      )}
      <div className={cn(
        "h-11 w-11 rounded-lg flex items-center justify-center mb-4",
        !disabled && selected ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
      )}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-semibold text-base">{title}</div>
      <div className="text-sm text-muted-foreground mt-1">{description}</div>
      <div className="text-xs text-muted-foreground mt-3">
        <span className="font-medium text-foreground">{count.toLocaleString()}</span> {countLabel}
      </div>
    </button>
  );
  if (!disabled) return card;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex-1">{card}</div>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  );
};

export default function OutreachNew() {
  const navigate = useNavigate();
  const { merchant } = useMerchant();

  const [step, setStep] = useState(1);
  const [mode, setMode] = useState<Mode>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [uploadRows, setUploadRows] = useState<UploadRow[] | null>(null);
  const [uploadFileName, setUploadFileName] = useState<string>("");
  const [parsing, setParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Step 2: outlets
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [loadingOutlets, setLoadingOutlets] = useState(false);
  const [selectedOutletId, setSelectedOutletId] = useState<string>("");

  // Step 3: channel
  const [channel, setChannel] = useState<Channel>(null);

  // Step 4: sequence + content
  const [sequenceEnabled, setSequenceEnabled] = useState(true);
  const [previewTab, setPreviewTab] = useState<"email" | "sms">("email");
  const [activeSequenceStep, setActiveSequenceStep] = useState(0); // 0,1,2
  const [steps, setSteps] = useState(() => DEFAULT_SEQUENCE_STEPS.map((s) => ({ ...s })));
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);
  const [savingDefaults, setSavingDefaults] = useState(false);

  useEffect(() => {
    if (step === 4) setPreviewTab(channel === "sms" ? "sms" : "email");
  }, [step, channel]);

  // Load saved merchant defaults once
  useEffect(() => {
    if (!merchant || defaultsLoaded) return;
    (async () => {
      const { data, error } = await epicengageSupabasev1
        .from("merchant_outreach_defaults")
        .select("steps, sequence_enabled")
        .eq("merchant_id", merchant.id)
        .maybeSingle();
      if (!error && data?.steps && Array.isArray(data.steps) && data.steps.length === 3) {
        setSteps(
          (data.steps as any[]).map((s, i) => ({
            label: s.label ?? DEFAULT_SEQUENCE_STEPS[i].label,
            day: typeof s.day === "number" ? s.day : DEFAULT_SEQUENCE_STEPS[i].day,
            subject: s.subject ?? DEFAULT_SEQUENCE_STEPS[i].subject,
            body: s.body ?? DEFAULT_SEQUENCE_STEPS[i].body,
            ctaLabel: s.ctaLabel ?? DEFAULT_SEQUENCE_STEPS[i].ctaLabel,
          })),
        );
        if (typeof data.sequence_enabled === "boolean") setSequenceEnabled(data.sequence_enabled);
      }
      setDefaultsLoaded(true);
    })();
  }, [merchant, defaultsLoaded]);

  const saveAsDefault = async () => {
    if (!merchant) return;
    setSavingDefaults(true);
    const { error } = await epicengageSupabasev1
      .from("merchant_outreach_defaults")
      .upsert(
        {
          merchant_id: merchant.id,
          sequence_enabled: sequenceEnabled,
          steps: steps as any,
        },
        { onConflict: "merchant_id" },
      );
    setSavingDefaults(false);
    if (error) toast.error("Couldn't save defaults");
    else toast.success("Saved as your default sequence");
  };

  useEffect(() => {
    if (step !== 2 || !merchant) return;
    setLoadingOutlets(true);
    (async () => {
      const { data, error } = await epicengageSupabasev1
        .from("outlets")
        .select("id, name, address, google_review_url")
        .eq("merchant_id", merchant.id)
        .eq("is_active", true)
        .order("created_at", { ascending: true });
      if (error) toast.error("Failed to load outlets");
      else setOutlets((data as Outlet[]) ?? []);
      setLoadingOutlets(false);
    })();
  }, [step, merchant]);

  const selectedOutlet = useMemo(
    () => outlets.find((o) => o.id === selectedOutletId) ?? null,
    [outlets, selectedOutletId],
  );

  useEffect(() => {
    if (mode !== "existing" || !merchant) return;
    if (customers.length > 0) return;
    setLoadingCustomers(true);
    (async () => {
      const { data, error } = await epicengageSupabasev1
        .from("customers")
        .select("id, name, email, phone, status")
        .eq("merchant_id", merchant.id)
        .eq("archived", false)
        .order("joined_at", { ascending: false })
        .limit(1000);
      if (error) {
        toast.error("Failed to load customers");
      } else {
        setCustomers((data as CustomerRow[]) || []);
      }
      setLoadingCustomers(false);
    })();
  }, [mode, merchant, customers.length]);

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        (c.name ?? "").toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.phone ?? "").toLowerCase().includes(q),
    );
  }, [customers, search]);

  const allFilteredSelected =
    filteredCustomers.length > 0 && filteredCustomers.every((c) => selectedIds.has(c.id));

  const toggleAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredCustomers.forEach((c) => next.delete(c.id));
      } else {
        filteredCustomers.forEach((c) => next.add(c.id));
      }
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const normalizeRow = (raw: Record<string, any>, idx: number): UploadRow => {
    const lower: Record<string, any> = {};
    Object.entries(raw).forEach(([k, v]) => {
      lower[k.toLowerCase().trim().replace(/[\s_-]+/g, "")] = v;
    });
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = lower[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
      }
      return "";
    };
    const first = pick("firstname", "fname", "givenname");
    const last = pick("lastname", "lname", "surname", "familyname");
    const fullName = pick("name", "fullname", "customername") || [first, last].filter(Boolean).join(" ");
    return {
      rowId: `r-${idx}-${Math.random().toString(36).slice(2, 8)}`,
      name: fullName,
      email: pick("email", "emailaddress", "mail", "primaryemail"),
      phone: pick("phone", "mobile", "phonenumber", "mobilephone", "tel", "telephone"),
    };
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setUploadFileName(file.name);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase();
      let rows: Record<string, any>[] = [];
      if (ext === "csv") {
        const text = await file.text();
        const parsed = Papa.parse<Record<string, any>>(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (h) => h.trim(),
        });
        rows = parsed.data;
      } else if (ext === "xlsx" || ext === "xls") {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, { defval: "" });
      } else {
        toast.error("Unsupported file type. Please upload .csv or .xlsx");
        setParsing(false);
        return;
      }
      const normalized = rows.map(normalizeRow).filter((r) => r.name || r.email || r.phone);
      setUploadRows(normalized);
      if (normalized.length === 0) {
        toast.error("No rows found in file");
      } else {
        toast.success(`Parsed ${normalized.length} row${normalized.length === 1 ? "" : "s"}`);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to parse file");
    } finally {
      setParsing(false);
    }
  };

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = "";
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const duplicateEmails = useMemo(() => {
    if (!uploadRows) return new Set<string>();
    const counts = new Map<string, number>();
    uploadRows.forEach((r) => {
      const e = r.email.toLowerCase().trim();
      if (e) counts.set(e, (counts.get(e) ?? 0) + 1);
    });
    return new Set(Array.from(counts.entries()).filter(([, n]) => n > 1).map(([e]) => e));
  }, [uploadRows]);

  const uploadStats = useMemo(() => {
    if (!uploadRows) return { valid: 0, missing: 0, dupes: 0 };
    let valid = 0;
    let missing = 0;
    uploadRows.forEach((r) => {
      const hasEmail = isValidEmail(r.email);
      const hasPhone = r.phone.trim().length >= 5;
      if (!hasEmail && !hasPhone) missing += 1;
      else valid += 1;
    });
    return { valid, missing, dupes: duplicateEmails.size };
  }, [uploadRows, duplicateEmails]);

  const removeUploadRow = (rowId: string) => {
    setUploadRows((rows) => rows?.filter((r) => r.rowId !== rowId) ?? null);
  };

  const audienceValid =
    (mode === "existing" && selectedIds.size > 0) ||
    (mode === "upload" && uploadRows !== null && uploadStats.valid > 0);

  const outletValid = !!selectedOutlet && !!selectedOutlet.google_review_url?.trim();

  // Audience reachability for Step 3
  const audienceCounts = useMemo(() => {
    let emails = 0;
    let phones = 0;
    if (mode === "existing") {
      customers.forEach((c) => {
        if (selectedIds.has(c.id)) {
          if (c.email && isValidEmail(c.email)) emails += 1;
          if (c.phone && c.phone.trim().length >= 5) phones += 1;
        }
      });
    } else if (mode === "upload" && uploadRows) {
      uploadRows.forEach((r) => {
        if (isValidEmail(r.email)) emails += 1;
        if (r.phone.trim().length >= 5) phones += 1;
      });
    }
    return { emails, phones };
  }, [mode, customers, selectedIds, uploadRows]);

  const channelValid =
    (channel === "email" && audienceCounts.emails > 0) ||
    (channel === "sms" && audienceCounts.phones > 0);

  // First-recipient preview context for Step 4
  const previewContext = useMemo(() => {
    let firstName = "Sarah";
    if (mode === "existing") {
      const first = customers.find((c) => selectedIds.has(c.id) && (c.name || c.email));
      if (first?.name) firstName = first.name.split(" ")[0];
      else if (first?.email) firstName = first.email.split("@")[0];
    } else if (mode === "upload" && uploadRows && uploadRows.length > 0) {
      const first = uploadRows.find((r) => r.name || r.email);
      if (first?.name) firstName = first.name.split(" ")[0];
      else if (first?.email) firstName = first.email.split("@")[0];
    }
    return {
      name: firstName,
      first_name: firstName,
      outlet_name: selectedOutlet?.name ?? "our shop",
    };
  }, [mode, customers, selectedIds, uploadRows, selectedOutlet]);

  const reviewUrl = selectedOutlet?.google_review_url ?? "https://g.page/r/your-review-link";

  const step4Valid =
    !!steps[0]?.body.trim() && !!steps[0]?.ctaLabel.trim() &&
    (channel === "sms" || !!steps[0]?.subject.trim());

  const [sending, setSending] = useState(false);
  const [sendMode, setSendMode] = useState<"now" | "schedule">("now");
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
  const [scheduleTime, setScheduleTime] = useState<string>("09:00");

  const scheduledAt = useMemo(() => {
    if (sendMode !== "schedule" || !scheduleDate) return null;
    const [h, m] = scheduleTime.split(":").map((n) => parseInt(n, 10) || 0);
    const d = new Date(scheduleDate);
    d.setHours(h, m, 0, 0);
    return d;
  }, [sendMode, scheduleDate, scheduleTime]);

  const scheduleInvalid = sendMode === "schedule" && (!scheduledAt || scheduledAt.getTime() <= Date.now() + 60_000);

  const canContinue =
    step === 1 ? audienceValid
      : step === 2 ? outletValid
      : step === 3 ? channelValid
      : step === 4 ? step4Valid
      : step === 5 ? !sending
      : false;

  const recipientCount =
    channel === "sms" ? audienceCounts.phones : audienceCounts.emails;

  const handleSend = async () => {
    if (!merchant || !selectedOutlet || !channel) {
      toast.error("Missing required data");
      return;
    }
    setSending(true);
    try {
      const campaignName = `${selectedOutlet.name} — ${new Date().toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

      // Build the recipient list from the chosen audience
      type RecipientInsert = { name: string | null; email: string | null; phone: string | null };
      let recipientsToInsert: RecipientInsert[] = [];
      if (mode === "existing") {
        recipientsToInsert = customers
          .filter((c) => selectedIds.has(c.id))
          .filter((c) =>
            channel === "email" ? !!(c.email && isValidEmail(c.email)) : !!(c.phone && c.phone.trim().length >= 5),
          )
          .map((c) => ({ name: c.name ?? null, email: c.email ?? null, phone: c.phone ?? null }));
      } else if (mode === "upload" && uploadRows) {
        recipientsToInsert = uploadRows
          .filter((r) =>
            channel === "email" ? isValidEmail(r.email) : r.phone.trim().length >= 5,
          )
          .map((r) => ({ name: r.name || null, email: r.email || null, phone: r.phone || null }));
      }

      if (recipientsToInsert.length === 0) {
        toast.error("No valid recipients to send to");
        setSending(false);
        return;
      }

      const isScheduled = sendMode === "schedule" && scheduledAt && scheduledAt.getTime() > Date.now();

      // 1. Create the blast (scheduled or draft)
      const { data: blast, error: blastErr } = await epicengageSupabasev1
        .from("blasts")
        .insert({
          merchant_id: merchant.id,
          outlet_id: selectedOutlet.id,
          review_url: selectedOutlet.google_review_url ?? null,
          channel,
          sequence_enabled: sequenceEnabled,
          status: isScheduled ? "scheduled" : "draft",
          scheduled_at: isScheduled ? scheduledAt!.toISOString() : null,
          name: campaignName,
          email_subject: steps[0]?.subject ?? null,
          body_step1: steps[0]?.body ?? null,
          body_step2: steps[1]?.body ?? null,
          body_step3: steps[2]?.body ?? null,
          cta_label: steps[0]?.ctaLabel ?? "Share Your Experience",
        })
        .select("id")
        .single();

      if (blastErr || !blast) throw blastErr ?? new Error("Failed to create blast");

      // 2. Bulk-insert recipients
      const { error: recErr } = await epicengageSupabasev1
        .from("blast_recipients")
        .insert(recipientsToInsert.map((r) => ({ ...r, blast_id: blast.id })));

      if (recErr) throw recErr;

      // 3. Trigger send pipeline now, or leave it for the scheduler
      if (isScheduled) {
        toast.success(`Campaign scheduled for ${format(scheduledAt!, "PPP 'at' p")}`);
      } else if (channel === "email") {
        const { error: invErr } = await epicengageSupabasev1.functions.invoke("send-blast", {
          body: { blast_id: blast.id },
        });
        if (invErr) throw invErr;
        toast.success("Campaign sent! Your messages are on their way.");
      } else {
        await epicengageSupabasev1
          .from("blasts")
          .update({ status: "sent", sent_at: new Date().toISOString() })
          .eq("id", blast.id);
        toast.success("Campaign sent! Your messages are on their way.");
      }

      navigate("/clientpanel/enagev1/outreach");
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to send campaign");
    } finally {
      setSending(false);
    }
  };

  const handleContinue = () => {
    if (step === 1) {
      setStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (step === 2) {
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (step === 3) {
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (step === 4) {
      setStep(5);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
  };

  const updateStep = (idx: number, patch: Partial<SequenceStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      requestCancel();
    }
  };

  const hasProgress =
    mode !== null ||
    selectedIds.size > 0 ||
    uploadRows !== null ||
    selectedOutletId !== "" ||
    channel !== null ||
    step > 1;

  const requestCancel = () => {
    if (hasProgress) setCancelOpen(true);
    else navigate("/clientpanel/enagev1/outreach");
  };

  return (
    <div>
      <div className="px-4 sm:px-6 md:px-10 py-8 mx-auto w-full">
        <div className="flex items-center justify-between gap-4 mb-8">
          <StepIndicator step={step} label={STEP_LABELS[step - 1]} />
          <button
            type="button"
            onClick={requestCancel}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
        </div>

        {step === 1 && (
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Who are you sending to?</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pick an existing CRM segment or upload a one-off list for this campaign.
          </p>

          <div className="mt-6 flex flex-col md:flex-row gap-4">
            <ChoiceCard
              selected={mode === "existing"}
              onClick={() => setMode("existing")}
              icon={Users}
              title="Select from existing customers"
              description="Choose from contacts already in your CRM"
            />
            <ChoiceCard
              selected={mode === "upload"}
              onClick={() => setMode("upload")}
              icon={Upload}
              title="Upload a fresh list"
              description="Import a CSV for this campaign only — won't be added to your CRM"
            />
          </div>

          {mode === "existing" && (
            <div className="mt-8 rounded-lg border bg-card">
              <div className="p-4 border-b flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, email, phone…"
                    className="pl-9"
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {selectedIds.size.toLocaleString()}
                  </span>{" "}
                  customer{selectedIds.size === 1 ? "" : "s"} selected
                </div>
              </div>

              <div className="max-h-[440px] overflow-auto">
                {loadingCustomers ? (
                  <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>
                ) : filteredCustomers.length === 0 ? (
                  <div className="p-12 text-center text-sm text-muted-foreground">
                    {customers.length === 0 ? "No customers in your CRM yet." : "No matches."}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={allFilteredSelected}
                            onCheckedChange={toggleAllFiltered}
                            aria-label="Select all"
                          />
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCustomers.map((c) => {
                        const checked = selectedIds.has(c.id);
                        return (
                          <TableRow
                            key={c.id}
                            className="cursor-pointer"
                            onClick={() => toggleOne(c.id)}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox checked={checked} onCheckedChange={() => toggleOne(c.id)} />
                            </TableCell>
                            <TableCell className="font-medium">{c.name || "—"}</TableCell>
                            <TableCell className="text-muted-foreground">{c.email}</TableCell>
                            <TableCell className="text-muted-foreground">{c.phone || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {STATUS_LABEL[(c.status as CustomerStatus) ?? "new"] ?? c.status ?? "—"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}

          {mode === "upload" && (
            <div className="mt-8 space-y-4">
              {!uploadRows ? (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "rounded-lg border-2 border-dashed p-12 text-center cursor-pointer transition-colors",
                    dragOver ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/30",
                  )}
                >
                  <FileSpreadsheet className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <div className="font-medium">
                    {parsing ? "Parsing…" : "Drop a CSV or XLSX file here"}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    or click to browse · max ~10,000 rows
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={onFileInput}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {uploadStats.valid === 0 && (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>No valid contacts found. Please check your file and try again.</span>
                    </div>
                  )}
                <div className="rounded-lg border bg-card">
                  <div className="p-4 border-b flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2 text-sm">
                      <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{uploadFileName}</span>
                      <span className="text-muted-foreground">· {uploadRows.length} rows</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200">
                        {uploadStats.valid} valid
                      </Badge>
                      {uploadStats.missing > 0 && (
                        <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">
                          {uploadStats.missing} missing contact
                        </Badge>
                      )}
                      {uploadStats.dupes > 0 && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200">
                          {uploadStats.dupes} duplicate emails
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setUploadRows(null);
                          setUploadFileName("");
                        }}
                      >
                        Replace file
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-[440px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead className="w-32">Issues</TableHead>
                          <TableHead className="w-12" />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadRows.map((r) => {
                          const emailLc = r.email.toLowerCase().trim();
                          const hasEmail = isValidEmail(r.email);
                          const hasPhone = r.phone.trim().length >= 5;
                          const missingBoth = !hasEmail && !hasPhone;
                          const isDupe = !!emailLc && duplicateEmails.has(emailLc);
                          return (
                            <TableRow
                              key={r.rowId}
                              className={cn(
                                missingBoth && "bg-destructive/5",
                                !missingBoth && isDupe && "bg-amber-50/60 dark:bg-amber-950/10",
                              )}
                            >
                              <TableCell>{r.name || <span className="text-muted-foreground">—</span>}</TableCell>
                              <TableCell className={cn(!hasEmail && r.email && "text-destructive")}>
                                {r.email || <span className="text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell>{r.phone || <span className="text-muted-foreground">—</span>}</TableCell>
                              <TableCell>
                                {missingBoth ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                    <AlertTriangle className="h-3 w-3" /> No contact
                                  </span>
                                ) : isDupe ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                                    <AlertTriangle className="h-3 w-3" /> Duplicate
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                  onClick={() => removeUploadRow(r.rowId)}
                                  aria-label="Remove row"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {step === 2 && (
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Which outlet is this campaign for?</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Customers will be directed to this outlet's Google review URL.
            </p>

            <div className="mt-6 max-w-xl space-y-4">
              {loadingOutlets ? (
                <p className="text-sm text-muted-foreground">Loading outlets…</p>
              ) : outlets.length === 0 ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
                    <div className="text-sm text-amber-900 dark:text-amber-100">
                      <p className="font-medium">No outlets yet</p>
                      <p className="mt-1">
                        Add at least one outlet (with a Google review URL) in{" "}
                        <Link to="/clientpanel/enagev1/settings" className="underline font-medium">Settings</Link>{" "}
                        before sending a campaign.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Outlet</label>
                    <Select value={selectedOutletId} onValueChange={setSelectedOutletId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose an outlet…" />
                      </SelectTrigger>
                      <SelectContent>
                        {outlets.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{o.name}</span>
                              {o.address && (
                                <span className="text-xs text-muted-foreground">{o.address}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedOutlet && (
                    <div className="rounded-lg border bg-card p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{selectedOutlet.name}</div>
                          {selectedOutlet.address && (
                            <div className="text-sm text-muted-foreground">{selectedOutlet.address}</div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Google review URL
                        </label>
                        {selectedOutlet.google_review_url ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={selectedOutlet.google_review_url}
                              readOnly
                              className="font-mono text-xs"
                            />
                            <Button asChild variant="outline" size="icon" aria-label="Open review URL">
                              <a href={selectedOutlet.google_review_url} target="_blank" rel="noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3">
                            <AlertCircle className="h-4 w-4 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-900 dark:text-amber-100">
                              No review URL set for this outlet. Please set it up in{" "}
                              <Link to="/clientpanel/enagev1/settings" className="underline font-medium">Settings</Link>{" "}
                              before sending.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">How do you want to reach them?</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Pick a delivery channel for this campaign.
            </p>

            <TooltipProvider delayDuration={200}>
              <div className="mt-6 flex flex-col md:flex-row gap-4">
                <ChannelCard
                  selected={channel === "email"}
                  disabled={audienceCounts.emails === 0}
                  disabledReason="Your list has no email addresses"
                  onClick={() => setChannel("email")}
                  icon={Mail}
                  title="Email"
                  description="Full message with open and click tracking"
                  count={audienceCounts.emails}
                  countLabel="with email"
                />
                <ChannelCard
                  selected={channel === "sms"}
                  disabled={audienceCounts.phones === 0}
                  disabledReason="Your list has no phone numbers"
                  onClick={() => setChannel("sms")}
                  icon={MessageSquare}
                  title="SMS"
                  description="Short message with click tracking. No open tracking."
                  count={audienceCounts.phones}
                  countLabel="with phone"
                />
              </div>
            </TooltipProvider>

            <div className="mt-6 flex items-start gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>SMS messages are auto-shortened to 160 characters from your email template.</p>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Preview & edit your messages</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Tweak the copy on the right — the sequence on the left updates automatically.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toast.success("Edits saved to this draft")}
                >
                  Save
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={saveAsDefault}
                  disabled={savingDefaults}
                >
                  {savingDefaults ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</>
                  ) : (
                    "Save as default"
                  )}
                </Button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* LEFT: sequence settings (40%) */}
              <div className="lg:col-span-2 space-y-4">
                <div className="rounded-lg border bg-card">
                  <div className="p-4 border-b flex items-center justify-between gap-3">
                    <div>
                      <div className="font-semibold text-sm">Follow-up Sequence</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Automatically nudge non-responders
                      </div>
                    </div>
                    <Switch checked={sequenceEnabled} onCheckedChange={setSequenceEnabled} />
                  </div>

                  <div className="divide-y">
                    {steps.map((s, idx) => {
                      const isInitial = idx === 0;
                      const isOpen = activeSequenceStep === idx;
                      const stepActive = isInitial || sequenceEnabled;
                      return (
                        <div key={idx}>
                          <button
                            type="button"
                            onClick={() => {
                              if (isInitial) setActiveSequenceStep(0);
                              else setActiveSequenceStep(isOpen ? -1 : idx);
                            }}
                            className={cn(
                              "w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors",
                              isOpen && "bg-muted/20",
                            )}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium">
                                Step {idx + 1} — Day {s.day}{" "}
                                <span className="text-muted-foreground font-normal">({s.label})</span>
                              </div>
                              <div className="text-xs text-muted-foreground truncate mt-0.5">
                                {s.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80)}
                              </div>
                            </div>
                            <Badge
                              variant="outline"
                              className={cn(
                                "shrink-0",
                                stepActive
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-200"
                                  : "bg-muted text-muted-foreground",
                              )}
                            >
                              {stepActive ? "Active" : "Disabled"}
                            </Badge>
                          </button>
                          {isOpen && stepActive && (
                            <div className="px-4 pb-4 space-y-3 bg-muted/10">
                              {channel === "email" && (
                                <div className="space-y-1.5">
                                  <Label className="text-xs">Subject</Label>
                                  <Input
                                    value={s.subject}
                                    onChange={(e) => updateStep(idx, { subject: e.target.value })}
                                  />
                                </div>
                              )}
                              <div className="space-y-1.5">
                                <Label className="text-xs">Body</Label>
                                {channel === "email" ? (
                                  <RichEmailEditor
                                    value={s.body}
                                    onChange={(html) => updateStep(idx, { body: html })}
                                    minHeight={220}
                                  />
                                ) : (
                                  <Textarea
                                    rows={5}
                                    value={s.body}
                                    onChange={(e) => updateStep(idx, { body: e.target.value })}
                                  />
                                )}
                              </div>
                              <div className="space-y-1.5">
                                <Label className="text-xs">CTA label</Label>
                                <Input
                                  value={s.ctaLabel}
                                  onChange={(e) => updateStep(idx, { ctaLabel: e.target.value })}
                                />
                              </div>
                              <p className="text-xs text-muted-foreground">
                                Variables: <code>{"{{name}}"}</code>, <code>{"{{outlet_name}}"}</code>
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* RIGHT: live preview (60%) */}
              <div className="lg:col-span-3">
                <Tabs value={previewTab} onValueChange={(v) => setPreviewTab(v as "email" | "sms")}>
                  <div className="flex items-center justify-between mb-3">
                    <TabsList>
                      <TabsTrigger value="email">Email Preview</TabsTrigger>
                      {channel === "sms" && <TabsTrigger value="sms">SMS Preview</TabsTrigger>}
                    </TabsList>
                    <span className="text-xs text-muted-foreground">
                      Previewing Step {activeSequenceStep + 1}
                    </span>
                  </div>

                  <TabsContent value="email" className="mt-0">
                    <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
                      <div className="bg-muted/40 border-b px-4 py-2 flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                        <span className="ml-3 text-xs text-muted-foreground truncate">
                          From: {merchant?.name ?? "Your Brand"} · To: {previewContext.name}
                        </span>
                      </div>
                      <div className="bg-[#f4f5f7] p-6">
                        <div className="mx-auto max-w-[560px] bg-white rounded-[14px] shadow-sm overflow-hidden">
                          <div className="px-10 pt-9">

                            <div className="text-xl font-semibold text-gray-900 leading-snug">
                              {renderTemplate(steps[activeSequenceStep].subject, previewContext)}
                            </div>
                            <div
                              className="mt-4 text-[15px] leading-[1.65] text-gray-800 prose prose-sm max-w-none prose-p:my-3 prose-headings:text-gray-900 prose-strong:text-gray-900 prose-a:text-blue-600"
                              dangerouslySetInnerHTML={{
                                __html: renderTemplate(steps[activeSequenceStep].body, previewContext),
                              }}
                            />
                            <div className="mt-7 flex flex-col items-center gap-2">
                              <div className="inline-flex items-center justify-center bg-[#2563eb] text-white font-semibold h-12 px-8 rounded-lg text-[15px]">
                                {steps[activeSequenceStep].ctaLabel}
                              </div>
                              <a
                                href={reviewUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] text-gray-400 hover:underline truncate max-w-xs break-all"
                              >
                                {reviewUrl}
                              </a>
                            </div>
                          </div>
                          <div className="px-10 pt-6 pb-7">
                            <div className="h-px bg-gray-200 mb-5" />
                            <div className="text-[13px] text-gray-500 leading-relaxed">
                              Thanks,
                              <br />
                              <span className="text-gray-700 font-semibold">
                                {previewContext.outlet_name}
                              </span>
                            </div>
                          </div>
                          <div className="px-10 pb-7 text-center">
                            <div className="text-[11px] text-gray-400">
                              Don't want these emails?{" "}
                              <span className="underline text-gray-500">Unsubscribe</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {channel === "sms" && (
                    <TabsContent value="sms" className="mt-0">
                      <div className="rounded-xl border bg-card p-6 flex justify-center">
                        <div className="w-full max-w-xs">
                          <div className="rounded-2xl bg-muted/40 p-4 space-y-3">
                            <div className="text-xs text-center text-muted-foreground">
                              {merchant?.name ?? "Your Brand"}
                            </div>
                            <div className="bg-background rounded-2xl rounded-tl-sm p-3 text-sm shadow-sm">
                              {shortenForSms(
                                steps[activeSequenceStep].body,
                                steps[activeSequenceStep].ctaLabel,
                                reviewUrl,
                                previewContext,
                              )}
                            </div>
                            <div className="text-[10px] text-center text-muted-foreground">
                              {shortenForSms(
                                steps[activeSequenceStep].body,
                                steps[activeSequenceStep].ctaLabel,
                                reviewUrl,
                                previewContext,
                              ).length}
                              /160 characters
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground text-center mt-3">
                            Auto-shortened from your email body.
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="max-w-2xl">
            <h1 className="text-2xl font-semibold tracking-tight">Ready to send?</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Quick check before your messages go out.
            </p>

            <div className="mt-6 rounded-xl border bg-card divide-y">
              <SummaryRow label="Recipients" value={`${recipientCount.toLocaleString()} contacts`} />
              <SummaryRow label="Outlet" value={selectedOutlet?.name ?? "—"} sub={selectedOutlet?.address ?? undefined} />
              <SummaryRow
                label="Channel"
                value={
                  <Badge variant="outline" className="font-medium">
                    {channel === "sms" ? (
                      <><MessageSquare className="h-3 w-3 mr-1" /> SMS</>
                    ) : (
                      <><Mail className="h-3 w-3 mr-1" /> Email</>
                    )}
                  </Badge>
                }
              />
              <SummaryRow
                label="Sequence"
                value={
                  sequenceEnabled
                    ? "Enabled — 3 steps over 14 days"
                    : "Disabled — one-time send"
                }
              />
              <SummaryRow
                label="Review URL"
                value={
                  <a
                    href={reviewUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary hover:underline inline-flex items-center gap-1.5 max-w-md truncate"
                  >
                    <span className="truncate">{reviewUrl}</span>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                  </a>
                }
              />
            </div>

            {/* Send timing */}
            <div className="mt-6 rounded-xl border bg-card p-5">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Clock className="h-4 w-4 text-muted-foreground" />
                When to send
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSendMode("now")}
                  className={cn(
                    "rounded-lg border px-3 py-3 text-left text-sm transition",
                    sendMode === "now"
                      ? "border-primary ring-1 ring-primary bg-primary/5"
                      : "hover:bg-muted/40",
                  )}
                >
                  <div className="font-medium">Send now</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Goes out immediately</div>
                </button>
                <button
                  type="button"
                  onClick={() => setSendMode("schedule")}
                  className={cn(
                    "rounded-lg border px-3 py-3 text-left text-sm transition",
                    sendMode === "schedule"
                      ? "border-primary ring-1 ring-primary bg-primary/5"
                      : "hover:bg-muted/40",
                  )}
                >
                  <div className="font-medium">Schedule for later</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Pick a date and time</div>
                </button>
              </div>

              {sendMode === "schedule" && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "mt-1 w-full justify-start text-left font-normal",
                            !scheduleDate && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {scheduleDate ? format(scheduleDate, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={scheduleDate}
                          onSelect={setScheduleDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs">Time</Label>
                    <Input
                      type="time"
                      value={scheduleTime}
                      onChange={(e) => setScheduleTime(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  {scheduleInvalid && (
                    <p className="sm:col-span-2 text-xs text-destructive">
                      Pick a date and time at least a minute in the future.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="mt-5 flex items-start gap-2 rounded-md bg-muted/40 border p-3">
              <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                {sendMode === "schedule" && scheduledAt && !scheduleInvalid
                  ? `Your initial send will go out on ${format(scheduledAt, "PPP 'at' p")}. Follow-up steps run automatically based on your sequence settings.`
                  : "Messages will be sent immediately. Follow-up steps will be sent automatically based on your sequence settings. Sending stops for each contact the moment they click your review link."}
              </p>
            </div>

            <Button
              onClick={handleSend}
              disabled={sending || scheduleInvalid}
              className="mt-6 w-full h-11 text-base"
            >
              {sending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {sendMode === "schedule" ? "Scheduling…" : "Sending…"}</>
              ) : sendMode === "schedule" ? (
                <>Schedule Campaign</>
              ) : (
                <>Send Campaign</>
              )}
            </Button>
          </div>
        )}

        <div className="mt-10 pt-6 border-t flex items-center justify-between">
          <Button variant="ghost" onClick={handleBack} disabled={sending}>
            {step === 1 ? "Cancel" : "Back"}
          </Button>
          {step < 5 && (
            <Button onClick={handleContinue} disabled={!canContinue}>
              Continue
            </Button>
          )}
        </div>
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Your campaign progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate("/clientpanel/enagev1/outreach")}>
              Discard campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
